import React, { useState, useEffect, useCallback } from 'react';
import { FileSignature, Plus, Trash2, Download, Send, Loader2, BookOpen, Info } from 'lucide-react';
import { apiClient } from '../../api/client';
import { InvestmentGrowthSlider } from '../investors/InvestmentGrowthSlider';

const TYPE_INFO = {
  safe: { label: 'SAFE', full: 'Simple Agreement for Future Equity (US)', tip: 'US standard. Money in now, shares issued automatically at the next priced round at a discount. No interest, no maturity, not debt.' },
  asa: { label: 'ASA', full: 'Advance Subscription Agreement (UK)', tip: 'UK equivalent of a SAFE. Pre-pays for shares issued at the next round or longstop date. Can qualify for SEIS/EIS tax relief (30-50% back) — no interest allowed.' },
  cln: { label: 'CLN', full: 'Convertible Loan Note (US/UK/EU)', tip: 'A loan that accrues interest and has a maturity date. Converts to shares at a discount on a qualifying round; repayable if none happens. For investors wanting downside protection.' },
};
const COUNTRIES = ['US', 'UK', 'EU'];
const STATUS_STYLE = {
  draft: { bg: '#F1EEE9', color: '#5C526A' },
  issued: { bg: '#FEF3C7', color: '#92400E' },
  converted: { bg: '#ECFDF5', color: '#059669' },
};

const money = (n) => `$${Number(n || 0).toLocaleString()}`;

const EMPTY = { type: 'asa', investor_name: '', investor_email: '', country: 'GB', amount_usd: 50000, discount_pct: 20, valuation_cap_usd: '', interest_rate_pct: 6, interest_years: 2, maturity_date: '', notes: '' };

export const InstrumentsPanel = () => {
  const [data, setData] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    try { setData(await apiClient.request('/api/vault/instruments/')); }
    catch (e) { setErr(e.message || 'Failed to load instruments'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setBusy('create'); setErr('');
    try {
      const body = { ...form, amount_usd: Number(form.amount_usd) || 0, discount_pct: Number(form.discount_pct) || 0 };
      if (form.valuation_cap_usd) body.valuation_cap_usd = Number(form.valuation_cap_usd); else delete body.valuation_cap_usd;
      if (form.type === 'cln') {
        body.interest_rate_pct = Number(form.interest_rate_pct) || 0;
        body.interest_years = Number(form.interest_years) || 2;
        if (!form.maturity_date) delete body.maturity_date;
      } else { delete body.interest_rate_pct; delete body.interest_years; delete body.maturity_date; }
      if (!body.investor_email) delete body.investor_email;
      if (!body.notes) delete body.notes;
      await apiClient.request('/api/vault/instruments/', { method: 'POST', body: JSON.stringify(body) });
      setForm(EMPTY); setShowForm(false); await load();
    } catch (e) { setErr(e.message || 'Create failed'); }
    setBusy('');
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this instrument record?')) return;
    setBusy(`del-${id}`);
    try { await apiClient.request(`/api/vault/instruments/${id}`, { method: 'DELETE' }); await load(); }
    catch (e) { setErr(e.message || 'Delete failed'); }
    setBusy('');
  };

  const downloadPdf = async (path, fname) => {
    setBusy(`dl-${path}`); setErr('');
    try {
      const token = apiClient.getToken();
      const res = await fetch(`${apiClient.baseUrl}${path}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = fname; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) { setErr(e.message || 'Download failed'); }
    setBusy('');
  };

  const sendToInvestor = async (inst) => {
    if (!window.confirm(`Email the ${inst.type.toUpperCase()} agreement PDF to ${inst.investor_email}?`)) return;
    setBusy(`send-${inst.instrument_id}`); setErr('');
    try {
      await apiClient.request(`/api/vault/instruments/${inst.instrument_id}/send`, { method: 'POST' });
      await load();
    } catch (e) { setErr(e.message || 'Send failed'); }
    setBusy('');
  };

  const engine = data?.engine || {};
  const instruments = data?.instruments || [];

  return (
    <div className="space-y-5" data-testid="instruments-panel">
      <div className="bg-gradient-to-r from-emerald-900 to-teal-900 rounded-2xl p-6 text-white">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-lg font-black mb-1 flex items-center gap-2"><FileSignature size={18} /> Investment Instruments — SAFE · ASA · CLN</h3>
            <p className="text-xs text-emerald-200 max-w-2xl">Record each early-investor agreement, generate the jurisdiction-aware contract PDF, and email it directly to the investor. Projections priced live from the canonical valuation engine.</p>
          </div>
          <button
            onClick={() => downloadPdf('/api/vault/instruments/guide.pdf', 'HiBFF-Founders-Guide-SAFE-ASA-CLN.pdf')}
            className="px-4 py-2 rounded-xl text-xs font-bold bg-white/15 hover:bg-white/25 transition-colors inline-flex items-center gap-1.5"
            data-testid="instruments-guide-btn">
            {busy === 'dl-/api/vault/instruments/guide.pdf' ? <Loader2 size={13} className="animate-spin" /> : <BookOpen size={13} />}
            Founder's Guide PDF
          </button>
        </div>
        <div className="flex flex-wrap gap-4 mt-4 text-xs">
          {Object.entries(TYPE_INFO).map(([k, t]) => (
            <span key={k} className="inline-flex items-center gap-1 bg-white/10 rounded-full px-3 py-1 cursor-help" title={t.tip} data-testid={`instrument-tooltip-${k}`}>
              <Info size={11} /> <b>{t.label}</b> — {t.full}
            </span>
          ))}
        </div>
      </div>

      {err && <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-700" data-testid="instruments-error">{err}</div>}

      <div className="bg-white border border-gray-200 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-bold text-gray-900">
            Register <span className="text-xs font-semibold text-gray-400 ml-1">{data?.totals?.count || 0} instruments · {money(data?.totals?.raised_usd)} committed</span>
          </h4>
          <button onClick={() => setShowForm(s => !s)} className="text-xs font-bold text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1" data-testid="instrument-add-btn">
            <Plus size={13} /> New instrument
          </button>
        </div>

        {showForm && (
          <div className="border border-emerald-200 bg-emerald-50/40 rounded-xl p-4 mb-4 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="instrument-form">
            <div className="col-span-2">
              <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1">Investor name</label>
              <input className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg" value={form.investor_name} onChange={e => setForm(f => ({ ...f, investor_name: e.target.value }))} data-testid="instrument-name" />
            </div>
            <div className="col-span-2">
              <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1">Investor email</label>
              <input className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg" value={form.investor_email} onChange={e => setForm(f => ({ ...f, investor_email: e.target.value }))} data-testid="instrument-email" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1">Type</label>
              <select className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg" value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))} data-testid="instrument-type">
                <option value="asa">ASA (UK)</option><option value="safe">SAFE (US)</option><option value="cln">CLN</option>
              </select>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1">Jurisdiction</label>
              <select className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg" value={form.country} onChange={e => setForm(f => ({ ...f, country: e.target.value }))} data-testid="instrument-country">
                {COUNTRIES.map(cc => <option key={cc} value={cc}>{cc}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1">Amount (USD)</label>
              <input type="number" className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg" value={form.amount_usd} onChange={e => setForm(f => ({ ...f, amount_usd: e.target.value }))} data-testid="instrument-amount" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1">Discount %</label>
              <input type="number" className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg" value={form.discount_pct} onChange={e => setForm(f => ({ ...f, discount_pct: e.target.value }))} data-testid="instrument-discount" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1">Valuation cap (optional)</label>
              <input type="number" className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg" value={form.valuation_cap_usd} onChange={e => setForm(f => ({ ...f, valuation_cap_usd: e.target.value }))} data-testid="instrument-cap" />
            </div>
            {form.type === 'cln' && (
              <>
                <div>
                  <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1">Interest % p.a.</label>
                  <input type="number" className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg" value={form.interest_rate_pct} onChange={e => setForm(f => ({ ...f, interest_rate_pct: e.target.value }))} data-testid="instrument-interest" />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1">Maturity date</label>
                  <input type="date" className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg" value={form.maturity_date} onChange={e => setForm(f => ({ ...f, maturity_date: e.target.value }))} data-testid="instrument-maturity" />
                </div>
              </>
            )}
            <div className="col-span-2 md:col-span-4 flex gap-2">
              <button onClick={create} disabled={!form.investor_name.trim() || busy === 'create'}
                className="px-5 py-2 rounded-xl text-xs font-bold text-white bg-emerald-700 hover:bg-emerald-800 disabled:opacity-50 inline-flex items-center gap-1.5"
                data-testid="instrument-create-btn">
                {busy === 'create' ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Create instrument
              </button>
              <button onClick={() => setShowForm(false)} className="px-4 py-2 rounded-xl text-xs font-semibold text-gray-500 hover:bg-gray-100">Cancel</button>
            </div>
          </div>
        )}

        {instruments.length === 0 ? (
          <p className="text-xs text-gray-400 italic py-4" data-testid="instruments-empty">No instruments yet — click "New instrument" to record your first SAFE, ASA or CLN.</p>
        ) : (
          <div className="space-y-2">
            {instruments.map(inst => {
              const st = STATUS_STYLE[inst.status] || STATUS_STYLE.draft;
              const proj = (inst.projection || [])[1] || (inst.projection || [])[0] || {};
              return (
                <div key={inst.instrument_id} className="border border-gray-100 rounded-xl p-3 flex flex-wrap items-center gap-3" data-testid={`instrument-row-${inst.instrument_id}`}>
                  <span className="px-2 py-1 rounded-lg text-[10px] font-black text-white cursor-help"
                    style={{ background: inst.type === 'safe' ? '#2563EB' : inst.type === 'asa' ? '#7B4FD8' : '#B45309' }}
                    title={TYPE_INFO[inst.type]?.tip}>
                    {inst.type.toUpperCase()} · {inst.country}
                  </span>
                  <div className="min-w-[140px]">
                    <div className="text-sm font-bold text-gray-900">{inst.investor_name}</div>
                    <div className="text-[10px] text-gray-400">{inst.investor_email || 'no email'}</div>
                  </div>
                  <div className="text-sm font-black text-gray-900">{money(inst.amount_usd)}</div>
                  <div className="text-xs text-gray-500">{inst.discount_pct}% disc{inst.valuation_cap_usd ? ` · cap ${money(inst.valuation_cap_usd)}` : ''}{inst.type === 'cln' ? ` · ${inst.interest_rate_pct || 0}% int` : ''}</div>
                  {proj.value !== undefined && (
                    <div className="text-xs font-semibold" style={{ color: '#0F8A4B' }} title={`Projected at ${Number(proj.users || 0).toLocaleString()} subscribers`}>
                      → {money(Math.round(proj.value))} ({proj.multiple}x)
                    </div>
                  )}
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ backgroundColor: st.bg, color: st.color }}>{inst.status}</span>
                  <div className="ml-auto flex items-center gap-1.5">
                    <button onClick={() => downloadPdf(`/api/vault/instruments/${inst.instrument_id}/agreement.pdf`, `HiBFF-${inst.type.toUpperCase()}-${inst.investor_name.replace(/\s+/g, '-')}.pdf`)}
                      className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-600" title="Download agreement PDF" data-testid={`instrument-pdf-${inst.instrument_id}`}>
                      {busy === `dl-/api/vault/instruments/${inst.instrument_id}/agreement.pdf` ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                    </button>
                    {inst.investor_email && (
                      <button onClick={() => sendToInvestor(inst)} className="p-1.5 rounded-lg hover:bg-emerald-50 text-emerald-700" title="Email agreement to investor" data-testid={`instrument-send-${inst.instrument_id}`}>
                        {busy === `send-${inst.instrument_id}` ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                      </button>
                    )}
                    <button onClick={() => remove(inst.instrument_id)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500" title="Delete" data-testid={`instrument-del-${inst.instrument_id}`}>
                      {busy === `del-${inst.instrument_id}` ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <InvestmentGrowthSlider
        floor={Number(engine.valuation_floor_usd) || 11_500_000}
        totalShares={Number(engine.total_shares) || 10_000_000}
        perUser={Number(engine.monthly_per_user_usd) || 26.49}
        multiple={Number(engine.revenue_multiple) || 12}
        anchors={engine.user_anchors || undefined}
        currency="$"
        variant="vault"
      />
    </div>
  );
};

export default InstrumentsPanel;
